import type { CodeViolationStep } from '../ai-reviewer-agent/submit-violations-tool';

import { removeDevModeFilterNote } from './dev-mode-filter-note';
import { stripFeedbackMarkers } from './feedback/constants';
import { removeInternalFindingSourceNote } from './internal-finding-source-note';

export {
  appendDevModeFilterNoteLine,
  DEV_MODE_FILTER_NOTE_END,
  DEV_MODE_FILTER_NOTE_HEADER,
  DEV_MODE_FILTER_NOTE_START,
  formatDeepDevModeFilterNoteLine,
  formatDevModeFilterNoteBlock,
  formatScreeningDevModeFilterNoteLine,
  parseDevModeFilterNote,
  removeDevModeFilterNote,
} from './dev-mode-filter-note';
export type { DevModeFilteringReason, ParsedDevModeFilterNote } from './dev-mode-filter-note';

export type AiReviewCommentMetadata = {
  confidence?: number;
  steps?: CodeViolationStep[];
};

// Metadata is embedded in GitHub comment HTML (`<!-- metadata:{...} -->`).
// GitHub enforces a 65 535-char body limit; these ceilings keep the JSON
// well under that while preserving enough detail for the thinking-steps UI.
const MAX_STEPS = 50;
const MAX_STEP_TEXT_LENGTH = 5_000;
const MAX_TOOL_ARG_LENGTH = 2_000;
const MAX_TOTAL_METADATA_JSON_LENGTH = 40_000;

export const METADATA_PREFIX = '<!-- metadata:';
const METADATA_SUFFIX = '-->';

// Regex to find the metadata block, allowing for optional newline at the end
const metadataRegex = new RegExp(
  `${METADATA_PREFIX.replace(/[.*+?^${}()|[\\]\\\]/g, '\\$&')}(\\{.*?\\})\\s*${METADATA_SUFFIX.replace(/[.*+?^${}()|[\\]\\\]/g, '\\$&')}\\n?`,
  's',
);
const htmlCommentRegex = /<!--[\s\S]*?-->\s*/g;

/**
 * Extracts metadata from a comment's content if present.
 *
 * The metadata is expected to be embedded as a JSON object inside an HTML comment,
 * e.g. <!-- metadata:{"confidence":0.9} -->
 *
 * @param content - The comment content, potentially containing a metadata block.
 * @returns The parsed metadata object, or undefined if not found or invalid.
 */
export const extractCommentMetadata = (content: string): AiReviewCommentMetadata | undefined => {
  const match = content.match(metadataRegex);

  if (match && match[1]) {
    try {
      return JSON.parse(match[1]);
    } catch (error) {
      console.error('Failed to parse metadata JSON:', error);
    }
  }
  return undefined;
};

/**
 * Escapes sequences that prematurely close HTML comments inside serialized JSON.
 *
 * The metadata JSON is embedded in `<!-- ... -->`. If the JSON contains literal `-->`
 * (e.g., mermaid syntax "A --> B" in AI thinking steps) or `--!>` (alternate comment
 * closer per WHATWG spec), HTML parsers close the comment early and the remaining
 * JSON leaks into the visible rendered output.
 *
 * We replace `>` after `--` or `--!` with the JSON unicode escape `\u003e`.
 * `JSON.parse` decodes `\u003e` back to `>` automatically, so extraction is lossless.
 */
function escapeHtmlCommentClose(json: string): string {
  return json.replace(/--(!?)>/g, '--$1\\u003e');
}

function truncate(value: string, max: number): string {
  return value.length <= max ? value : `${value.slice(0, max)}…[truncated]`;
}

function truncateToolArgs(args: unknown): unknown {
  if (typeof args === 'string') {
    return truncate(args, MAX_TOOL_ARG_LENGTH);
  }
  if (args === null || args === undefined) {
    return args;
  }
  if (Array.isArray(args)) {
    return args.map((item) => truncateToolArgs(item));
  }
  if (typeof args === 'object') {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(args as Record<string, unknown>)) {
      out[k] = typeof v === 'string' ? truncate(v, MAX_TOOL_ARG_LENGTH) : truncateToolArgs(v);
    }
    return out;
  }
  return args;
}

/**
 * Sanitizes metadata so the serialized JSON is safe to embed in an HTML comment.
 *
 * Guards against:
 *  - Oversized step text (AI thinking can be 10k+ chars)
 *  - Oversized tool-call args (bash commands, file contents)
 *  - Unbounded step arrays
 *  - Total JSON exceeding ~40 KB (well under GitHub's 65 535-char comment limit)
 */
export function sanitizeMetadataForComment(
  metadata: AiReviewCommentMetadata,
): AiReviewCommentMetadata {
  if (!metadata.steps || metadata.steps.length === 0) {
    return metadata;
  }

  const cappedSteps = metadata.steps.slice(0, MAX_STEPS).map((step) => ({
    ...(typeof step.text === 'string' ? { text: truncate(step.text, MAX_STEP_TEXT_LENGTH) } : {}),
    ...(step.toolCalls
      ? {
          toolCalls: step.toolCalls.map((tc) => ({
            ...(tc.toolName !== undefined ? { toolName: tc.toolName } : {}),
            ...(tc.input !== undefined ? { input: truncateToolArgs(tc.input) } : {}),
          })),
        }
      : {}),
  }));

  const sanitized: AiReviewCommentMetadata = {
    ...metadata,
    steps: cappedSteps,
  };

  const json = JSON.stringify(sanitized);
  if (json.length > MAX_TOTAL_METADATA_JSON_LENGTH) {
    return { confidence: metadata.confidence };
  }

  return sanitized;
}

/**
 * Encodes metadata into a comment's content.
 *
 * The metadata is embedded as a JSON object inside an HTML comment at the beginning of the content,
 * e.g. <!-- metadata:{"confidence":0.9} -->
 *
 * @param content - The original comment content.
 * @param metadata - The metadata object to encode.
 * @returns The content with metadata embedded at the beginning.
 */
export const encodeCommentMetadata = (
  content: string,
  metadata: AiReviewCommentMetadata,
): string => {
  const sanitized = sanitizeMetadataForComment(metadata);
  const metadataString = escapeHtmlCommentClose(JSON.stringify(sanitized));
  const metadataComment = `${METADATA_PREFIX}${metadataString} ${METADATA_SUFFIX}\n`; // Ensure space before suffix

  // Check if content already has metadata
  if (metadataRegex.test(content)) {
    // Replace existing metadata
    return content.replace(metadataRegex, metadataComment);
  }

  // Add metadata at the beginning
  return `${metadataComment}${content}`;
};

/**
 * Removes metadata comments from violation content
 */
export function removeMetadataFromComment(content: string): string {
  return content.replace(metadataRegex, '');
}

/**
 * Removes hidden HTML comments from violation content after metadata has been parsed.
 */
export function removeHiddenHtmlCommentsFromComment(content: string): string {
  return content.replace(htmlCommentRegex, '');
}

/**
 * Extracts the violation body from a comment that contains AI context.
 * This removes the <details> section that contains the "Prompt for AI agents" dropdown,
 * as well as any DEV MODE annotations and the internal finding-source note.
 *
 * @param content - The full comment content that may include AI context
 * @returns The violation body without the AI context section or DEV MODE annotations
 */
export function extractViolationBodyFromComment(content: string): string {
  // First remove any metadata
  const withoutMetadata = removeMetadataFromComment(content);

  // Remove DEV MODE annotations and the finding-source note before stripping HTML
  // comments, since both internal notes are delimited by HTML comment markers.
  const withoutDevMode = removeDevModeFilterNote(removeInternalFindingSourceNote(withoutMetadata));

  // Remove any remaining hidden markers before this content is shown or reused in AI prompts
  const withoutHtmlComments = removeHiddenHtmlCommentsFromComment(withoutDevMode);

  // `[FEEDBACK_USED]` is an internal signal that only `processFeedbackMarker` is meant to
  // consume (it swaps the marker for feedback links on the inline comment). Callers of this
  // helper reuse the body on surfaces that never run that step — the PR summary's aggregated
  // "Prompt for AI agents" block, the Slack fix prompt, dashboard previews, and the
  // `rawViolation` column — so strip it here too or the raw marker ships to users.
  const withoutFeedbackMarkers = stripFeedbackMarkers(withoutHtmlComments);

  // Find the start of the <details> section that contains the AI context
  const detailsIndex = withoutFeedbackMarkers.indexOf('<details>');

  if (detailsIndex === -1) {
    // No AI context section found, return the content as is
    return withoutFeedbackMarkers.trim();
  }

  // Extract content before the <details> section
  return withoutFeedbackMarkers.substring(0, detailsIndex).trim();
}

/** Drops the "P1:" priority label cubic puts at the start of each posted finding. */
export function removePriorityLabel(text: string): string {
  return text.replace(/^P\d+\s*:\s*/i, '');
}

/** The finding's text for an LLM prompt: the comment body without its leading "P1:" label. */
export function extractViolationTextFromComment(content: string): string {
  return removePriorityLabel(extractViolationBodyFromComment(content)).trim();
}
